/**
 * Shared SDK singleton (no integration imports). Used by thin HTTP modules and stores
 * that must not import the full `client.js` graph.
 */
// Production (Vite/Rolldown): API subclasses must not run before BaseApi is defined in the chunk.
import '@repo/sdk/api/base.api.js';
import { createSDK } from '@repo/sdk';
import { useAuthStore } from '@/store/auth.store.js';
import { useTenantStore } from '@/store/tenant.store.js';
import { isJwtExpired } from '../auth/jwt.js';
import { api } from '@/app/config.js';
import i18n from '@/lib/i18n.js';
import { Logger } from '@/lib/app-log.js';

const SDK_DEBUG = import.meta.env.VITE_SDK_DEBUG === 'true';

const tokenStore = {
    get: () => useAuthStore.getState().token,
    getRefreshToken: () => useAuthStore.getState().refreshToken,
    getTenantId: () => useTenantStore.getState().selectedTenantId,
    set: (token) => {
        const store = useAuthStore.getState();
        if (store.setAuthenticated) {
            store.setAuthenticated(token, store.refreshToken, store.user);
        } else if (store.setToken) {
            store.setToken(token);
        }
    },
    setTokens: ({ accessToken, refreshToken }) => {
        const store = useAuthStore.getState();
        if (store.setAuthenticated) {
            store.setAuthenticated(accessToken, refreshToken, store.user);
        }
    },
    clear: () => {
        const store = useAuthStore.getState();
        if (store.setUnauthenticated) store.setUnauthenticated();
        else if (store.logout) store.logout();
    },
    shouldSkipAuthRefresh: () => {
        const { isLoggingOut, token, refreshToken } = useAuthStore.getState();
        if (isLoggingOut) return true;
        if (!token && !refreshToken) return true;
        if (token && !isJwtExpired(token)) return true;
        return false;
    },
};

if (SDK_DEBUG) {
    Logger.info('[SDK Client] Initializing with:', { baseURL: api.origin });
}

export const sdk = createSDK({
    baseURL: api.origin,
    tokenStore,
    // Browser: avoid axios-retry 1s backoff stalls on pagination/list GETs (localhost/network blips).
    retries: 0,
});

sdk.client.interceptors.request.use((req) => {
    const tenantId = useTenantStore.getState().selectedTenantId;
    if (SDK_DEBUG) {
        Logger.info(`[SDK Client] Interceptor - URL: ${req.url}, TenantID: ${tenantId}`);
    }
    if (tenantId) {
        req.headers['X-Tenant-Id'] = tenantId;
    }
    const locale = i18n.language?.split('-')[0];
    if (locale) {
        req.headers['Accept-Language'] = locale;
    }
    return req;
});

sdk.client.interceptors.response.use(undefined, (error) => {
    const code = error?.response?.data?.code;
    if (code === 'ACCOUNT_SUSPENDED' || code === 'ACCOUNT_INACTIVE') {
        useAuthStore.getState().setUnauthenticated?.();
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
            const reason = code === 'ACCOUNT_SUSPENDED' ? 'suspended' : 'inactive';
            window.location.assign(`/login?account=${reason}`);
        }
    }
    return Promise.reject(error);
});

export async function httpGet(path, params) {
    return sdk.get(path, { params });
}

export async function httpPost(path, data) {
    return sdk.post(path, data);
}

export async function httpPut(path, data) {
    return sdk.put(path, data);
}

export async function httpDelete(path) {
    return sdk.delete(path);
}

export async function httpPatch(path, data) {
    const response = await sdk.client.patch(path, data);
    return response.data;
}
